import {
  TITLE_FONT_LIBRARY,
  TITLE_FONT_LIBRARY_BY_ID,
  buildTitleFontFaceCss,
  getTitleFontEntry,
  resolveTitleFontSelection,
} from './fontLibrary'

const stylesheetLoads = new Map()
const inlineLoads = new Map()

function ensureBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function ensureStylesheet(href) {
  if (!ensureBrowser() || !href) return Promise.resolve()
  if (stylesheetLoads.has(href)) return stylesheetLoads.get(href)

  const promise = new Promise((resolve, reject) => {
    const existing = Array.from(document.querySelectorAll('link[data-vyby-font-href]'))
      .find((node) => node.dataset.vybyFontHref === href)
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', (event) => reject(event), { once: true })
      return
    }

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    link.dataset.vybyFontHref = href
    link.addEventListener('load', () => {
      link.dataset.loaded = 'true'
      resolve()
    }, { once: true })
    link.addEventListener('error', (event) => reject(event), { once: true })
    document.head.appendChild(link)
  })

  stylesheetLoads.set(href, promise)
  return promise
}

function ensureInlineFaces(entry) {
  if (!ensureBrowser() || !entry) return Promise.resolve()
  if (inlineLoads.has(entry.id)) return inlineLoads.get(entry.id)

  const promise = Promise.resolve().then(() => {
    const styleId = `vyby-font-inline-${entry.id}`
    if (document.getElementById(styleId)) return
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = buildTitleFontFaceCss(entry)
    document.head.appendChild(style)
  })

  inlineLoads.set(entry.id, promise)
  return promise
}

function ensureSourceLoaded(entry) {
  if (!entry) return Promise.resolve()
  if (entry.sourceType === 'stylesheet') return ensureStylesheet(entry.href)
  if (entry.sourceType === 'faces') return ensureInlineFaces(entry)
  return Promise.resolve()
}

export async function ensureTitleFontLoaded(fontId, requestedWeight = null, requestedStyle = 'normal') {
  const selection = resolveTitleFontSelection(fontId, requestedWeight, requestedStyle)
  const entry = TITLE_FONT_LIBRARY_BY_ID[selection.id] || getTitleFontEntry(selection.id)
  await ensureSourceLoaded(entry)

  if (ensureBrowser() && document.fonts?.load) {
    const spec = `${selection.style} ${selection.weight} 32px "${selection.family}"`
    try {
      await document.fonts.load(spec)
      await document.fonts.ready
    } catch (error) {
      console.warn('title font load failed', selection.id, error)
    }
  }

  return selection
}

export async function preloadTitleFonts(fontIds = []) {
  const uniqueIds = Array.from(new Set(fontIds.filter(Boolean)))
  await Promise.all(uniqueIds.map((fontId) => ensureTitleFontLoaded(fontId)))
}

export function installTitleFontDebugHelper() {
  if (!ensureBrowser()) return
  window.vybyTitleFonts = {
    list() {
      return TITLE_FONT_LIBRARY.map((entry) => ({
        id: entry.id,
        label: entry.label,
        family: entry.family,
        provider: entry.provider,
        weights: entry.weights,
      }))
    },
    get(fontId) {
      return getTitleFontEntry(fontId)
    },
    async load(fontId, weight = null, style = 'normal') {
      return ensureTitleFontLoaded(fontId, weight, style)
    },
  }
}

